export type LinkMode = "standard" | "hires";

/**
 * The four URLs a search engine has to offer.
 *
 * Framed and linked are separated because they are genuinely different requests: the
 * framed one carries whatever the engine needs in order to render inside an iframe, and
 * that has no business on a link somebody opens in a real tab.
 */
export interface SearchProviderInterface {
  getIframeStandard(item: string): string;
  getIframeHires(item: string): string;
  getLinkStandard(item: string): string;
  getLinkHires(item: string): string;
}

/**
 * The shared half of a provider.
 *
 * Two things come from here. A link is the framed URL unless a provider says otherwise —
 * most engines have nothing to say about the difference, so requiring all four methods
 * from every one of them would mean two existing only to repeat the others. And the
 * query building, so a subclass writes what it wants asked and not how to spell it.
 */
export abstract class SearchProvider implements SearchProviderInterface {
  /** The endpoint every URL this provider builds points at. */
  protected abstract readonly base: string;

  abstract getIframeStandard(item: string): string;
  abstract getIframeHires(item: string): string;

  getLinkStandard(item: string): string {
    return this.getIframeStandard(item);
  }

  getLinkHires(item: string): string {
    return this.getIframeHires(item);
  }

  /**
   * A URL on this provider's endpoint, built rather than concatenated.
   *
   * `URL` parses the base instead of trusting it, so a typo in one of the endpoints below
   * throws where it is written rather than producing a string that only fails once
   * somebody clicks it. `searchParams` then encodes every value, which the hand-written
   * version also did correctly — but by repeating an `encodeURIComponent` call in each of
   * six builders, which is six chances to forget rather than none.
   *
   * Spaces come out as `+` and colons as `%3A` where the old strings spelled them
   * literally. Both decode back to the same thing, which is what the tests pin.
   */
  protected search(params: Record<string, string>): string {
    const url = new URL(this.base);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  }

  /** What is actually searched for: the name, as art. */
  protected term(item: string): string {
    return `${item} art`;
  }
}

/*
 * `igu=1` is what lets Google's results render inside an iframe at all, and `biw`/`bih`
 * are the pane's dimensions. All three describe the frame, so Google is the one provider
 * here that overrides the link methods.
 */
export class GoogleImages extends SearchProvider {
  protected readonly base = "https://www.google.com/search";

  getIframeStandard(item: string): string {
    return this.search({
      igu: "1",
      hl: "en",
      q: this.term(item),
      biw: "1043",
      bih: "670",
      source: "lnms",
      tbm: "isch",
    });
  }

  getIframeHires(item: string): string {
    return this.search({
      igu: "1",
      hl: "en",
      q: this.term(item),
      tbm: "isch",
      tbs: "isz:l",
    });
  }

  override getLinkStandard(item: string): string {
    return this.search({
      hl: "en",
      q: this.term(item),
      source: "lnms",
      tbm: "isch",
    });
  }

  override getLinkHires(item: string): string {
    return this.search({
      hl: "en",
      q: this.term(item),
      tbm: "isch",
      tbs: "isz:l",
    });
  }
}

/** No framing parameter to strip, so the inherited link methods stand. */
export class DuckDuckGoImages extends SearchProvider {
  protected readonly base = "https://duckduckgo.com/";

  getIframeStandard(item: string): string {
    return this.search({
      q: this.term(item),
      iar: "images",
      iax: "images",
      ia: "images",
    });
  }

  getIframeHires(item: string): string {
    return this.search({
      q: this.term(item),
      iar: "images",
      iax: "images",
      ia: "images",
      iaf: "size:imagesize-wallpaper",
    });
  }
}

/** Likewise: nothing here is about the frame. */
export class BingImages extends SearchProvider {
  protected readonly base = "https://www.bing.com/images/search";

  getIframeStandard(item: string): string {
    return this.search({
      q: this.term(item),
      qs: "n",
      form: "QBLH",
      scope: "images",
      sp: "-1",
      sc: "0-24",
      sk: "",
      cvid: "CA1EABE9C89D49429BD718A4EDE5BD69",
    });
  }

  getIframeHires(item: string): string {
    return this.search({
      scope: "images",
      sp: "-1",
      sc: "0-24",
      sk: "",
      q: this.term(item),
      // A leading space, which the old string spelled as a literal `+`.
      qft: " filterui:imagesize-wallpaper",
      FORM: "IRFLTR",
    });
  }
}

export const searchProviders = {
  Google: new GoogleImages(),
  DuckDuckGo: new DuckDuckGoImages(),
  Bing: new BingImages(),
};

export type Provider = keyof typeof searchProviders;

/** The URL the results pane frames. */
export function iframeUrl(
  provider: SearchProviderInterface,
  mode: LinkMode,
  item: string
): string {
  return mode === "hires"
    ? provider.getIframeHires(item)
    : provider.getIframeStandard(item);
}

/** The URL the All and Large buttons open in a real browser tab. */
export function linkUrl(
  provider: SearchProviderInterface,
  mode: LinkMode,
  item: string
): string {
  return mode === "hires"
    ? provider.getLinkHires(item)
    : provider.getLinkStandard(item);
}
